
import { cn } from '@/lib/utils';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Linkedin } from 'lucide-react';

interface TeamMember {
  name: string;
  position: string;
  bio: string;
  image: string;
  calendly: string;
  social: {
    linkedin: string;
  };
}

interface TeamMembersSectionProps {
  teamVisible: boolean;
  coFounders: TeamMember[];
}

const TeamMembersSection = ({ teamVisible, coFounders }: TeamMembersSectionProps) => {
  const isMobile = useIsMobile();

  return (
    <section className="py-6 md:py-10 px-4 bg-gradient-to-b from-background to-secondary/10" id="team-members">
      <div className="max-w-6xl mx-auto">
        <div className={cn(
          "relative",
          teamVisible ? "opacity-100" : "opacity-0",
          "transition-all duration-700"
        )}>
          {/* Simplified decorative background elements */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-10 -left-10 w-64 h-64 rounded-full bg-primary/5 -z-10 blur-2xl"></div>
            <div className="absolute -bottom-10 -right-10 w-64 h-64 rounded-full bg-primary/5 -z-10 blur-2xl"></div>
          </div>
          
          {/* Founder portraits container - vertical layout */}
          <div className="space-y-16">
            {/* First founder */}
            <div className={cn(
              "transition-all duration-700",
              teamVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            )}>
              <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                {/* Portrait */}
                <div className="relative w-64 sm:w-72 md:w-80 flex-shrink-0">
                  <div className="relative rounded-lg overflow-hidden shadow-lg">
                    <AspectRatio ratio={3/4} className="bg-muted">
                      <img 
                        src={coFounders[0].image} 
                        alt={coFounders[0].name}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Subtle overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-60"></div>
                      
                      {/* Name and position */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white text-center bg-gradient-to-t from-black/60 to-transparent">
                        <h3 className="text-lg font-bold tracking-wide">{coFounders[0].name}</h3>
                        <p className="text-white/90 text-sm">{coFounders[0].position}</p>
                      </div>
                    </AspectRatio>
                  </div>
                </div>
                
                {/* Quote/Words */}
                <div className="flex-1 text-center lg:text-left">
                  <blockquote className="text-xl md:text-2xl font-medium text-foreground leading-relaxed mb-4">
                    "You don't need to speak tech to get great software built. Tell us what slows you down in plain words, and we'll turn it into something your team will actually want to use."
                  </blockquote>
                  <cite className="text-muted-foreground">— {coFounders[0].name}</cite>
                </div>
              </div>
            </div>

            {/* Second founder */}
            <div className={cn(
              "transition-all duration-700",
              teamVisible ? "opacity-100 translate-y-0 delay-300" : "opacity-0 translate-y-10"
            )}>
              <div className="flex flex-col lg:flex-row-reverse items-center gap-8 lg:gap-12">
                {/* Portrait */}
                <div className="relative w-64 sm:w-72 md:w-80 flex-shrink-0">
                  <div className="relative rounded-lg overflow-hidden shadow-lg">
                    <AspectRatio ratio={3/4} className="bg-muted">
                      <img 
                        src={coFounders[1].image} 
                        alt={coFounders[1].name}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Subtle overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-60"></div>
                      
                      {/* Name and position */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white text-center bg-gradient-to-t from-black/60 to-transparent">
                        <h3 className="text-lg font-bold tracking-wide">{coFounders[1].name}</h3>
                        <p className="text-white/90 text-sm">{coFounders[1].position}</p>
                      </div>
                    </AspectRatio>
                  </div>
                </div>
                
                {/* Quote/Words */}
                <div className="flex-1 text-center lg:text-right">
                  <blockquote className="text-xl md:text-2xl font-medium text-foreground leading-relaxed mb-4">
                    "Good software should fit the way you already work, not the other way around. We build exactly what you need, nothing you don't, and it should feel simple from day one."
                  </blockquote>
                  <cite className="text-muted-foreground">— {coFounders[1].name}</cite>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile connecting element and social links */}
          {isMobile && (
            <div className={cn(
              "transition-all duration-700 pt-4 pb-2",
              teamVisible ? "opacity-100 translate-y-0 delay-150" : "opacity-0 translate-y-10"
            )}>
              <div className="flex items-center justify-center gap-4 mb-3">
                <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent flex-1"></div>
                <span className="bg-primary/10 text-primary text-xs px-3 py-1 rounded-full font-medium">Connect</span>
                <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent flex-1"></div>
              </div>
              
              <div className="flex justify-center space-x-8">
                <a 
                  href={coFounders[0].social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1"
                >
                  <div className="p-2 rounded-full bg-primary/10 text-primary">
                    <Linkedin className="h-4 w-4" />
                  </div>
                  <span className="text-xs text-muted-foreground">{coFounders[0].name}</span>
                </a>
                
                <a 
                  href={coFounders[1].social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1"
                >
                  <div className="p-2 rounded-full bg-primary/10 text-primary">
                    <Linkedin className="h-4 w-4" />
                  </div>
                  <span className="text-xs text-muted-foreground">{coFounders[1].name}</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TeamMembersSection;
